import {
  addObject,
  beginGesture,
  cancelGesture,
  commitGesture,
  duplicateSelected,
  editLevel,
  editorViewport,
  eraseAt,
  exportLevel,
  freshArena,
  hitTest,
  importLevel,
  loadLibrary,
  makeObject,
  moveSelected,
  moveSpawn,
  PIECES,
  pieceFor,
  redo,
  resizeSelected,
  resizeCorners,
  resizeFromCorner,
  selectionIndices,
  selectObjects,
  selectObjectsInBox,
  toggleObjectSelection,
  type ResizeCorner,
  rotateSelected,
  saveLibrary,
  screenToEditor,
  selectSpawnAt,
  selectedHasDuplicate,
  shareHash,
  snap,
  storeDraft,
  targetAt,
  undo,
  type EditorState,
  type ToolGroup,
} from './editor';
import { createSimWorld, type SimHandle } from '../sim/world';
import { createCamera } from '../render/camera';
import { buildFrame, COLORS } from '../render/buildFrame';
import { createCanvasRenderer } from '../render/canvas/renderer';
import { THEMES, type ThemeId } from '../sim/level/themes';
import { checkArena } from './checks';
import type { LevelDef, LevelObject } from '../sim/level/schema';
import { levelCapacity } from '../levels/catalog';
import { buttonLabel, type ControllerGlyphs, type InputDevice } from '../input/presentation';
import './maker.css';

export type EditorTestOptions = {
  fromCursor?: { x: number; y: number };
  /** Total fighters, including the person testing; remaining seats are bots. Defaults to two. */
  fighters?: number;
};
export type EditorViewFns = {
  playtest: (level: LevelDef, device?: string, options?: EditorTestOptions) => void;
  back: () => void;
  getInput: () => InputDevice;
  onKeyboard: () => void;
  getGlyphs: () => ControllerGlyphs;
};
export type EditorView = { dispose: () => void };
type Mode = 'build' | 'grab' | 'erase';

export function mountEditor(root: HTMLElement, state: EditorState, fns: EditorViewFns): EditorView {
  root.innerHTML = '';
  const abort = new AbortController(),
    signal = abort.signal;
  const wrap = document.createElement('section');
  wrap.className = 'maker';
  wrap.setAttribute('aria-label', 'Arena maker');
  wrap.innerHTML = `
    <header class="maker-header">
      <button data-action="back" class="maker-back" aria-label="Back">‹ <span>Back</span></button>
      <div class="maker-heading"><span class="maker-kicker">FLOPPY CLASH</span><h1>Arena maker</h1></div>
      <label class="maker-name">Arena name<input aria-label="Arena name" maxlength="48" /></label>
      <button data-action="undo" aria-label="Undo">↶</button>
      <button data-action="redo" aria-label="Redo" title="Redo · Ctrl/⌘ Shift Z">↷</button>
      <button data-action="save">Save arena</button>
      <button data-action="test-cursor" aria-label="Test from cursor">⌖ Test here</button>
      <button data-action="test" class="maker-primary" aria-label="Test arena">▶ Test arena</button>
    </header>
    <div class="maker-stage">
      <canvas class="maker-scene" aria-hidden="true"></canvas>
      <canvas class="maker-canvas" aria-label="Arena canvas" tabindex="0"></canvas>
      <div class="maker-modebar" role="group" aria-label="Editing tools">
        <button data-mode="build" aria-label="Build" aria-pressed="true">▰ Build</button>
        <button data-mode="grab" aria-label="Grab" aria-pressed="false">✋ Grab</button>
        <button data-mode="erase" aria-label="Erase" aria-pressed="false">⌫ Erase</button>
        <button data-action="grid" aria-pressed="true">Grid</button>
      </div>
      <div class="maker-stage-actions"><button data-action="test-party" hidden></button><button data-action="checks">Arena checks</button><button data-action="arenas">My arenas</button><button data-action="settings">Arena settings</button></div>
      <div class="maker-selection" aria-live="polite"><strong></strong><span></span><div class="maker-transform" role="group" aria-label="Selected piece size and rotation">
        <button data-action="narrow" aria-label="Narrower">W −</button><button data-action="widen" aria-label="Wider">W +</button>
        <button data-action="shorten" aria-label="Shorter">H −</button><button data-action="heighten" aria-label="Taller">H +</button>
        <button data-action="rotate" aria-label="Rotate piece">↻ Rotate</button>
        <button data-action="duplicate" aria-label="Duplicate piece">⧉ Copy</button>
        <button data-action="clear-selection" aria-label="Clear selection">Clear group</button>
      </div></div>
      <div class="maker-context" aria-live="polite"><strong></strong><span></span></div>
      <aside class="maker-panel" hidden></aside>
      <span class="maker-coordinate" aria-hidden="true"></span>
    </div>
    <div class="maker-toolbox">
      <div class="maker-shelf-heading"><div class="maker-categories" role="group" aria-label="Piece categories"></div><span class="maker-piece-hint"></span></div>
      <div class="maker-pieces" role="group" aria-label="Pieces"></div>
    </div>
    <footer class="maker-footer"><span class="maker-controls"></span><span class="maker-status" role="status" aria-live="polite">Empty space builds. Existing pieces move. No tool switch needed.</span></footer>`;
  root.append(wrap);
  const $ = <T extends HTMLElement>(selector: string) => wrap.querySelector<T>(selector)!;
  const canvas = $<HTMLCanvasElement>('.maker-canvas'),
    scene = $<HTMLCanvasElement>('.maker-scene');
  const stage = $('.maker-stage'),
    panel = $('.maker-panel'),
    name = $<HTMLInputElement>('.maker-name input');
  const pieces = $('.maker-pieces'),
    categories = $('.maker-categories'),
    controls = $('.maker-controls');
  const status = $('.maker-status'),
    selection = $('.maker-selection'),
    prompt = $('.maker-context');
  const context = canvas.getContext('2d')!;
  const renderer = createCanvasRenderer(scene);
  let sim: SimHandle | null = null,
    disposed = false,
    dirty = true,
    overlayDirty = true,
    raf = 0;
  let mode: Mode = 'build',
    category: ToolGroup = pieceFor(state.tool).group,
    gridVisible = true;
  let view = editorViewport(state.level.bounds, 1, 1);
  let cursor = {
    x: state.level.bounds.x + state.level.bounds.w / 2,
    y: state.level.bounds.y + state.level.bounds.h / 2,
  };
  let pointer = false,
    gestureMode: Mode | 'resize' | 'select' = 'build',
    padGrab = false,
    padPainting = false,
    padUI = false,
    padActive = fns.getInput().kind === 'gamepad',
    copying = false;
  let activePointer: number | null = null,
    pointerOffset = { x: 0, y: 0 };
  let resizeDrag: { original: LevelObject; corner: ResizeCorner } | null = null;
  let marquee: { start: { x: number; y: number }; base: number[] } | null = null;
  let resizeRepeatAt = 0,
    resizeDirection = '',
    padResizing = false;
  const painted = new Set<string>();
  let library: LevelDef[] = [],
    panelKind = '';
  const padButtons = new Map<number, boolean[]>();
  for (const pad of navigator.getGamepads?.() ?? [])
    if (pad)
      padButtons.set(
        pad.index,
        pad.buttons.map((b) => b.pressed),
      );
  let previousPad = -1,
    inputKey = '';
  let repeatAt = 0,
    previousDirection = '';
  name.value = state.level.name;

  function announce(message: string) {
    status.textContent = message;
  }
  function changed(message?: string) {
    dirty = overlayDirty = true;
    name.value = state.level.name;
    if (!storeDraft(state)) announce('Draft storage is unavailable. Use Export to keep a copy.');
    else if (message) announce(message);
    refreshSelection();
    wrap.dataset.objects = String(state.level.objects.length);
    wrap.dataset.spawns = String(state.level.spawns.length);
    const capacity = levelCapacity(state.level),
      partyTest = $<HTMLButtonElement>('[data-action="test-party"]');
    partyTest.hidden = capacity <= 4;
    partyTest.textContent = `Test with ${capacity} fighters`;
    partyTest.title = `You and ${capacity - 1} bots · Uses the arena's player starts`;
    $<HTMLButtonElement>('[data-action="undo"]').disabled = !state.history.length && !state.gesture;
    $<HTMLButtonElement>('[data-action="redo"]').disabled = !state.future.length;
  }
  function finishGesture() {
    const canceledCopy = copying && selectedHasDuplicate(state);
    if (canceledCopy) cancelGesture(state);
    pointer = false;
    resizeDrag = null;
    marquee = null;
    activePointer = null;
    padGrab = padPainting = copying = padResizing = false;
    painted.clear();
    if (commitGesture(state))
      changed(
        `${state.level.objects.length} pieces · Up to ${levelCapacity(state.level)} fighters · Draft saved`,
      );
    else if (canceledCopy) changed('Copy canceled. Move a copy to a new spot before dropping.');
    else refreshSelection();
  }
  function cancelHeld() {
    if (!padGrab && !pointer) return false;
    if (marquee) selectObjects(state, marquee.base);
    cancelGesture(state);
    pointer = false;
    resizeDrag = null;
    marquee = null;
    activePointer = null;
    padGrab = padPainting = copying = padResizing = false;
    painted.clear();
    changed('Move canceled. Your piece is back where it started.');
    return true;
  }
  function selectMode(next: Mode) {
    finishGesture();
    mode = next;
    wrap
      .querySelectorAll<HTMLButtonElement>('[data-mode]')
      .forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.mode === next)));
    if (padUI) closePanel();
    refreshSelection();
    overlayDirty = true;
  }
  function pickTool(id: string) {
    finishGesture();
    state.tool = id;
    category = pieceFor(id).group;
    mode = 'build';
    selectMode('build');
    paintShelf();
    overlayDirty = true;
    if (!padUI) canvas.focus({ preventScroll: true });
  }
  function button(label: string, action: () => void, className = '') {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = label;
    b.className = className;
    b.addEventListener('click', action, { signal });
    return b;
  }
  function paintShelf() {
    const focused = document.activeElement as HTMLElement | null;
    const focusedCategory = focused?.dataset.category,
      focusedTool = focused?.dataset.tool;
    categories.replaceChildren();
    pieces.replaceChildren();
    for (const group of ['Terrain', 'Traps', 'Toys', 'Spawns'] as ToolGroup[]) {
      const b = button(group, () => {
        category = group;
        paintShelf();
      });
      b.dataset.category = group;
      b.setAttribute('aria-pressed', String(category === group));
      categories.append(b);
    }
    for (const p of PIECES.filter((p) => p.group === category)) {
      const b = button('', () => pickTool(p.id), 'maker-piece');
      const icon = document.createElement('span'),
        label = document.createElement('span');
      icon.className = 'maker-piece-icon';
      icon.textContent = p.icon;
      icon.setAttribute('aria-hidden', 'true');
      label.textContent = p.name;
      b.append(icon, label);
      b.title = p.hint;
      b.dataset.tool = p.id;
      b.setAttribute('aria-label', p.name);
      b.setAttribute('aria-pressed', String(state.tool === p.id));
      pieces.append(b);
    }
    $('.maker-piece-hint').textContent = pieceFor(state.tool).hint;
    if (focusedCategory)
      categories
        .querySelector<HTMLButtonElement>(`[data-category="${focusedCategory}"]`)
        ?.focus({ preventScroll: true });
    else if (focusedTool)
      pieces
        .querySelector<HTMLButtonElement>(`[data-tool="${focusedTool}"]`)
        ?.focus({ preventScroll: true });
    refreshSelection();
  }
  function refreshSelection() {
    const count = selectionIndices(state).length;
    const obj = state.level.objects[state.selected],
      start = state.level.spawns[state.selectedSpawn];
    const title = selection.querySelector('strong')!,
      detail = selection.querySelector('span')!;
    title.textContent =
      count > 1
        ? `${count} pieces selected`
        : start
          ? `Player start ${state.selectedSpawn + 1}`
          : obj
            ? pieceFor(obj.type).name
            : pieceFor(state.tool).name;
    detail.textContent =
      count > 1
        ? 'Move and copy together · Shift-click or left stick click to change the group'
        : start
          ? 'Pick up to move · Four starts are always kept'
          : obj
            ? `${obj.w ?? 2} × ${obj.h ?? 1} · ${Math.round(((obj.angle ?? 0) * 180) / Math.PI)}°`
            : mode === 'build'
              ? 'Empty space builds · Existing pieces move'
              : mode === 'grab'
                ? 'Click a piece to move it'
                : 'Click a piece to erase it';
    $('.maker-transform').hidden = !obj;
    selection.querySelectorAll<HTMLButtonElement>('[data-action]').forEach((b) => {
      b.disabled =
        count > 1 && b.dataset.action !== 'duplicate' && b.dataset.action !== 'clear-selection';
    });
    canvas.dataset.selectionCount = String(count);
    updateControls();
  }
  function updateControls() {
    const device = fns.getInput(),
      label = (index: number) => buttonLabel(index, device, fns.getGlyphs());
    padActive = device.kind === 'gamepad';
    const holding = padGrab || (pointer && gestureMode === 'grab'),
      painting = padPainting || (pointer && gestureMode === 'build'),
      hovered = targetAt(state, cursor.x, cursor.y),
      interaction = holding ? 'drop' : mode === 'erase' ? 'erase' : hovered ? 'grab' : 'place';
    controls.textContent = padActive
      ? padUI
        ? `✚ Choose · ${label(0)} Select · ${label(1)} Build · ${label(9)} Test`
        : `${label(4)} / ${label(5)} Pieces · ${label(2)} Erase · ${label(3)} Undo · ${label(9)} Test · ${label(1)} ${holding ? 'Cancel' : 'Toolbox'}`
      : 'Drag handles Resize · Shift-click / Shift-drag Group · Alt-drag Copy · Ctrl/⌘ Z Undo · Shift+Enter Test here';
    const targetName =
      hovered?.kind === 'spawn'
        ? 'player start'
        : hovered
          ? pieceFor(state.level.objects[hovered.index]!.type).name.toLowerCase()
          : pieceFor(state.tool).name.toLowerCase();
    prompt.hidden = padUI;
    prompt.querySelector('strong')!.textContent = holding
      ? `${padActive ? label(0) : pointer ? 'Release' : 'Space'} Drop ${copying ? 'copy' : 'piece'}`
      : painting && mode !== 'erase'
        ? `Painting ${pieceFor(state.tool).name.toLowerCase()}`
        : interaction === 'grab'
          ? `${padActive ? label(0) : 'Drag / Space'} Move ${targetName}`
          : interaction === 'erase'
            ? `${padActive ? label(0) : 'Click'} Erase ${hovered ? targetName : 'piece'}`
            : mode === 'grab'
              ? 'Point at a piece to move it'
              : `${padActive ? label(0) : 'Click / Space'} Place ${targetName}`;
    prompt.querySelector('span')!.textContent = holding
      ? `${padActive ? label(1) : 'Esc'} Cancel · ${padActive ? label(7) : 'R'} Rotate`
      : painting && mode !== 'erase'
        ? `${padActive ? `Release ${label(0)}` : 'Release'} to finish this row`
        : hovered
          ? `${padActive ? label(11) : 'Alt-drag / D'} Copy · ${padActive ? label(7) : 'R'} Rotate`
          : mode === 'grab'
            ? 'Build also picks pieces up directly'
            : padActive
              ? 'Hold and move to paint a row'
              : 'Drag across empty space to paint';
    if (!holding && !painting && selectionIndices(state).length) {
      prompt.querySelector('span')!.textContent = padActive
        ? `${label(10)} Group · Right stick Resize · ${label(11)} Copy · ${label(10)} + ${label(9)} Test here`
        : 'Drag corner handles to resize · Shift-click Group · Shift+Enter Test here';
    }
    if (resizeDrag) prompt.querySelector('strong')!.textContent = 'Release to finish resizing';
    if (marquee) prompt.querySelector('strong')!.textContent = 'Select pieces inside the box';
    const location = screen(cursor.x, cursor.y);
    prompt.style.left = `${Math.max(8, Math.min(view.width - 254, location.x + 22))}px`;
    prompt.style.top = `${Math.max(64, Math.min(view.height - 76, location.y + 20))}px`;
    canvas.style.cursor = holding ? 'grabbing' : interaction === 'grab' ? 'grab' : 'crosshair';
    $<HTMLButtonElement>('[data-action="undo"]').title =
      `Undo · ${padActive ? label(3) : 'Ctrl/⌘ Z'}`;
    canvas.dataset.cursorX = String(cursor.x);
    canvas.dataset.cursorY = String(cursor.y);
    canvas.dataset.tool = state.tool;
    canvas.dataset.mode = mode;
    canvas.dataset.interaction = interaction;
    canvas.dataset.holding = String(holding);
    $('.maker-coordinate').textContent =
      `${cursor.x}, ${cursor.y}${holding ? ' · Holding piece' : ''}`;
  }
  function closePanel() {
    panel.hidden = true;
    panelKind = '';
    padUI = false;
    wrap.classList.remove('maker-toolbox-focus');
    canvas.focus({ preventScroll: true });
    updateControls();
  }
  function panelHeader(title: string) {
    finishGesture();
    panelKind = title;
    panel.hidden = false;
    panel.replaceChildren();
    padUI = true;
    updateControls();
    const heading = document.createElement('div');
    heading.className = 'maker-panel-heading';
    const h = document.createElement('h2');
    h.textContent = title;
    heading.append(h, button('Done', closePanel));
    panel.append(heading);
    heading.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
  }
  function showSettings() {
    const focusedTheme = (document.activeElement as HTMLElement | null)?.dataset.theme;
    panelHeader('Arena settings');
    const themeTitle = document.createElement('h3');
    themeTitle.textContent = 'Pick the mood';
    panel.append(themeTitle);
    const themeGrid = document.createElement('div');
    themeGrid.className = 'maker-themes';
    for (const [id, theme] of Object.entries(THEMES)) {
      const b = button(id[0]!.toUpperCase() + id.slice(1), () => {
        editLevel(state, () => {
          state.level.theme = id as ThemeId;
        });
        changed('New mood, same arena.');
        showSettings();
      });
      b.dataset.theme = id;
      b.style.setProperty('--theme-swatch', theme.backgroundTop);
      b.setAttribute('aria-pressed', String(state.level.theme === id));
      themeGrid.append(b);
    }
    panel.append(themeGrid);
    const help = document.createElement('p');
    help.textContent = `Up to ${levelCapacity(state.level)} fighters · ${state.level.spawns.length} player starts. Add eight spaced flags for a full party. Your draft saves as you build.`;
    panel.append(help);
    const drops = button(
      state.level.drops?.enabled === false ? 'Random weapon drops: Off' : 'Random weapon drops: On',
      () => {
        editLevel(state, () => {
          state.level.drops = {
            xMin: state.level.bounds.x + 4,
            xMax: state.level.bounds.x + state.level.bounds.w - 4,
            intervalScale: 1,
            ...state.level.drops,
            enabled: state.level.drops?.enabled === false,
          };
        });
        changed();
        showSettings();
      },
    );
    panel.append(drops, button('Import / export', showTransfer));
    if (focusedTheme)
      panel
        .querySelector<HTMLButtonElement>(`[data-theme="${focusedTheme}"]`)
        ?.focus({ preventScroll: true });
  }
  async function showArenas() {
    panelHeader('My arenas');
    const loading = document.createElement('p');
    loading.textContent = 'Loading your arenas…';
    panel.append(loading);
    try {
      library = await loadLibrary();
      if (disposed || panelKind !== 'My arenas') return;
      loading.remove();
      if (!library.length) {
        const empty = document.createElement('p');
        empty.textContent = 'Your shelf is empty. Save this arena to start your collection.';
        panel.append(empty);
      }
      for (const level of library.sort((a, b) => a.name.localeCompare(b.name))) {
        const b = button(
          level.name,
          () => {
            importLevel(state, JSON.stringify(level));
            changed(`Loaded ${level.name}. Undo returns to your previous draft.`);
            closePanel();
          },
          'maker-saved-arena',
        );
        const detail = document.createElement('small');
        detail.textContent = `${level.theme} · Up to ${levelCapacity(level)} fighters · ${level.objects.length} pieces`;
        b.append(detail);
        panel.append(b);
      }
      panel.append(
        button('New arena', () => {
          editLevel(state, () => {
            state.level = freshArena();
            state.selected = state.selectedSpawn = -1;
            state.selection = [];
          });
          changed('Fresh arena ready. Undo returns to your previous draft.');
          closePanel();
        }),
        button('Save a copy', () => {
          editLevel(state, () => {
            state.level.id = `user-${crypto.randomUUID()}`;
            state.level.name = `${state.level.name} copy`;
          });
          name.value = state.level.name;
          void saveArena();
        }),
        button('New large arena (8 fighters)', () => {
          editLevel(state, () => {
            state.level = freshArena('large');
            state.selected = state.selectedSpawn = -1;
            state.selection = [];
          });
          changed('Large arena ready with eight starts. Undo returns to your previous draft.');
          closePanel();
        }),
        button('Save arena', () => {
          void saveArena();
        }),
        button('Import / export', showTransfer),
      );
      panel.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
    } catch {
      if (!disposed) {
        loading.textContent =
          'Could not open saved arenas. Use Import / export to keep a file copy.';
        panel.append(button('Import / export', showTransfer));
      }
    }
  }
  async function saveArena() {
    finishGesture();
    editLevel(state, () => {
      state.level.name = name.value.trim() || 'My arena';
      if (state.level.id === 'user-draft') state.level.id = `user-${crypto.randomUUID()}`;
    });
    try {
      await saveLibrary(state.level);
      if (!disposed) {
        changed(`Saved “${state.level.name}” to My arenas.`);
        if (panelKind === 'My arenas') void showArenas();
      }
    } catch {
      if (!disposed)
        announce('Save failed. Use Arena settings → Import / export to keep a file copy.');
    }
  }
  function showTransfer() {
    panelHeader('Import / export');
    const p = document.createElement('p');
    p.textContent =
      'Share an arena file, or open one a friend made. Importing keeps the current draft in Undo.';
    panel.append(p);
    const file = document.createElement('input');
    file.type = 'file';
    file.accept = '.json,application/json';
    file.setAttribute('aria-label', 'Import arena file');
    file.addEventListener(
      'change',
      async () => {
        const selected = file.files?.[0];
        if (!selected) return;
        try {
          if (selected.size > 1_000_000) throw new Error('Choose an arena file smaller than 1 MB.');
          const raw = await selected.text();
          if (disposed) return;
          importLevel(state, raw);
          changed(`Imported ${state.level.name}.`);
          closePanel();
        } catch (error) {
          announce(
            error instanceof Error && error.message.startsWith('Choose')
              ? error.message
              : 'Could not import this arena. Check that it is a Floppy Clash arena file with four player starts.',
          );
        }
      },
      { signal },
    );
    panel.append(
      file,
      button('Export arena file', () => {
        try {
          const url = URL.createObjectURL(
            new Blob([exportLevel(state)], { type: 'application/json' }),
          );
          const a = document.createElement('a');
          a.href = url;
          a.download = `${state.level.name.replace(/[^a-z0-9-]/gi, '-') || 'arena'}.json`;
          a.click();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
          announce('Arena file exported.');
        } catch {
          announce('Could not export this arena. Check its pieces and player starts.');
        }
      }),
      button('Create share link', () => {
        const input = document.createElement('input');
        input.readOnly = true;
        input.setAttribute('aria-label', 'Arena share link');
        input.value = `${location.origin}${location.pathname}#l=${shareHash(state)}`;
        panel.append(input);
        input.focus();
        input.select();
        announce('Share link ready to copy.');
      }),
    );
  }
  function showChecks() {
    panelHeader('Arena checks');
    const intro = document.createElement('p');
    intro.textContent =
      'Build advice, not a pass/fail test. Landing and route checks are geometric estimates; moving platforms, wall jumps and timing need playtesting.';
    panel.append(intro);
    const checks = checkArena(state.level);
    if (!checks.length) {
      const clear = document.createElement('p');
      clear.textContent =
        'No obvious starting-space, terrain-overlap or route issues found. Test the arena to judge how it plays.';
      panel.append(clear);
    }
    for (const check of checks) {
      const card = document.createElement('section');
      card.className = 'maker-check';
      const title = document.createElement('h3'),
        detail = document.createElement('p');
      title.textContent = check.title;
      detail.textContent = check.detail;
      card.append(title, detail);
      if (check.at)
        card.append(
          button('Show location', () => {
            cursor = clamped(check.at!);
            closePanel();
            overlayDirty = true;
            updateControls();
            announce(check.title);
          }),
        );
      panel.append(card);
    }
    panel.append(
      button('Test arena', () => testArena()),
      button('Test from cursor', () => testArena(true)),
    );
  }
  function testArena(fromCursor = false, fighters = 2) {
    finishGesture();
    name.blur();
    try {
      const level = JSON.parse(exportLevel(state)) as LevelDef;
      storeDraft(state);
      const input = fns.getInput(),
        pad = input.kind === 'gamepad' ? navigator.getGamepads?.()[input.index] : null;
      fns.playtest(
        level,
        pad ? `pad:${pad.index}` : 'keyboard',
        { fighters, ...(fromCursor ? { fromCursor: { ...cursor } } : {}) },
      );
    } catch {
      announce('This arena needs valid pieces and at least four player starts before testing.');
    }
  }
  function transform(kind: string) {
    const obj = state.level.objects[state.selected];
    if (!obj) return;
    if (selectionIndices(state).length > 1) {
      announce(
        'Resize or rotate one piece at a time. Shift-click or left stick click to change the group.',
      );
      return;
    }
    if (kind === 'rotate') rotateSelected(state, Math.PI / 4);
    else
      resizeSelected(
        state,
        (obj.w ?? 2) + (kind === 'widen' ? state.grid : kind === 'narrow' ? -state.grid : 0),
        (obj.h ?? 1) + (kind === 'heighten' ? state.grid : kind === 'shorten' ? -state.grid : 0),
      );
    changed();
  }
  wrap.addEventListener(
    'click',
    (event) => {
      const b = (event.target as HTMLElement).closest<HTMLButtonElement>('button');
      if (!b) return;
      if (b.dataset.mode) {
        selectMode(b.dataset.mode as Mode);
        return;
      }
      const action = b.dataset.action;
      if (action === 'back') {
        finishGesture();
        fns.back();
      }
      if (action === 'test') testArena();
      if (action === 'test-cursor') testArena(true);
      if (action === 'test-party') testArena(false, levelCapacity(state.level));
      if (action === 'checks') showChecks();
      if (action === 'clear-selection') {
        finishGesture();
        selectObjects(state, []);
        refreshSelection();
        overlayDirty = true;
      }
      if (action === 'save') void saveArena();
      if (action === 'arenas') void showArenas();
      if (action === 'settings') showSettings();
      if (action === 'undo' || action === 'redo') {
        finishGesture();
        (action === 'undo' ? undo : redo)(state);
        changed(action === 'undo' ? 'Undid the last edit.' : 'Redid the last edit.');
      }
      if (action === 'grid') {
        gridVisible = !gridVisible;
        b.setAttribute('aria-pressed', String(gridVisible));
        overlayDirty = true;
      }
      if (action && ['narrow', 'widen', 'shorten', 'heighten', 'rotate'].includes(action))
        transform(action);
      if (action === 'duplicate') {
        const selected = state.level.objects[state.selected];
        if (selected) {
          cursor = { x: selected.x, y: selected.y };
          startCopy();
          setToolbox(false);
        }
      }
    },
    { signal },
  );
  name.addEventListener(
    'change',
    () => {
      editLevel(state, () => {
        state.level.name = name.value.trim() || 'My arena';
      });
      changed();
    },
    { signal },
  );

  function clamped(p: { x: number; y: number }) {
    const b = state.level.bounds;
    return {
      x: Math.max(b.x, Math.min(b.x + b.w, snap(p.x, state.grid))),
      y: Math.max(b.y, Math.min(b.y + b.h, snap(p.y, state.grid))),
    };
  }
  function rawPoint(event: PointerEvent) {
    const r = canvas.getBoundingClientRect();
    return screenToEditor(
      view,
      ((event.clientX - r.left) * view.width) / r.width,
      ((event.clientY - r.top) * view.height) / r.height,
    );
  }
  function cornerAt(event: PointerEvent) {
    if (selectionIndices(state).length !== 1) return null;
    const obj = state.level.objects[state.selected],
      p = rawPoint(event);
    return obj
      ? (resizeCorners(obj).find(
          (handle) => Math.hypot(handle.x - p.x, handle.y - p.y) * view.scale <= 11,
        )?.corner ?? null)
      : null;
  }
  function toggleAtCursor() {
    finishGesture();
    const target = targetAt(state, cursor.x, cursor.y);
    if (target?.kind === 'object') toggleObjectSelection(state, target.index);
    else selectObjects(state, []);
    overlayDirty = true;
    refreshSelection();
    announce(
      `${selectionIndices(state).length} pieces selected. Pick up a selected piece to move the group.`,
    );
  }
  function point(event: PointerEvent) {
    const r = canvas.getBoundingClientRect();
    // CSS transforms and letterboxing are both accounted for before the world conversion.
    return clamped(
      screenToEditor(
        view,
        ((event.clientX - r.left) * view.width) / r.width,
        ((event.clientY - r.top) * view.height) / r.height,
      ),
    );
  }
  function atCursor(which: Mode) {
    if (which === 'erase') {
      const result = eraseAt(state, cursor.x, cursor.y);
      if (result === 'minimum-spawns')
        announce('Keep four player starts. Grab a flag to move it instead.');
      if (result === 'removed') changed();
      return;
    }
    if (which === 'build') {
      const key = `${state.tool}:${cursor.x}:${cursor.y}`;
      if (painted.has(key)) return;
      if (
        state.level.objects.length >= 1200 &&
        state.tool !== 'spawn' &&
        state.tool !== 'drop-range'
      ) {
        announce('This arena is full. Erase a few pieces to make room.');
        return;
      }
      painted.add(key);
      const result = addObject(state, cursor.x, cursor.y);
      if (result === 'spawn-limit') {
        announce('All 32 player starts are placed. Grab an existing flag to move it.');
        return;
      }
      changed(
        result === 'duplicate'
          ? 'Already placed here. Pick it up to move or resize it.'
          : undefined,
      );
    }
  }
  function grab(copy = false) {
    if (selectSpawnAt(state, cursor.x, cursor.y) < 0 && hitTest(state, cursor.x, cursor.y) < 0) {
      state.selected = state.selectedSpawn = -1;
      state.selection = [];
      refreshSelection();
      return false;
    }
    beginGesture(state);
    if (copy) {
      const result = duplicateSelected(state);
      if (result !== 'copied') {
        cancelGesture(state);
        announce(
          result === 'spawn-limit'
            ? 'All 32 player starts are placed. Move an existing flag instead.'
            : 'This arena is full. Erase a piece to make room for a copy.',
        );
        return false;
      }
      copying = true;
      changed();
    }
    const p = state.level.spawns[state.selectedSpawn] ?? state.level.objects[state.selected]!;
    pointerOffset = { x: cursor.x - p.x, y: cursor.y - p.y };
    refreshSelection();
    overlayDirty = true;
    return true;
  }
  function startCopy() {
    finishGesture();
    padGrab = grab(true);
    refreshSelection();
    overlayDirty = true;
    if (padGrab) announce('Copy in hand. Move it, then drop it in a new spot.');
  }
  function primaryAction(paint: boolean) {
    if (padGrab) {
      finishGesture();
      return;
    }
    if (mode !== 'erase' && grab()) {
      padGrab = true;
      announce('Piece in hand. Move it, then drop it.');
      refreshSelection();
    } else if (mode !== 'grab') {
      beginGesture(state);
      padPainting = paint;
      painted.clear();
      atCursor(mode);
      if (!paint) finishGesture();
    }
    overlayDirty = true;
  }
  function rotateAtCursor(reverse = false) {
    if (!padGrab && !(pointer && gestureMode === 'grab')) {
      const hovered = targetAt(state, cursor.x, cursor.y);
      if (hovered?.kind !== 'object') return;
      hitTest(state, cursor.x, cursor.y);
    }
    if (selectionIndices(state).length > 1) {
      announce('Rotate one piece at a time. Change the selected group first.');
      return;
    }
    rotateSelected(state, reverse ? -Math.PI / 4 : Math.PI / 4);
    changed();
  }
  function moveGrab() {
    if (state.selectedSpawn >= 0)
      moveSpawn(state, state.selectedSpawn, cursor.x - pointerOffset.x, cursor.y - pointerOffset.y);
    else moveSelected(state, cursor.x - pointerOffset.x, cursor.y - pointerOffset.y);
    changed();
  }
  function paintSegment(before: { x: number; y: number }) {
    const target = { ...cursor },
      dx = target.x - before.x,
      dy = target.y - before.y;
    const count = Math.max(1, Math.ceil(Math.max(Math.abs(dx), Math.abs(dy)) / state.grid));
    for (let i = 1; i <= count; i++) {
      cursor = clamped({ x: before.x + (dx * i) / count, y: before.y + (dy * i) / count });
      if (gestureMode === 'build' || gestureMode === 'erase') atCursor(gestureMode);
    }
    cursor = target;
  }
  canvas.addEventListener(
    'pointerdown',
    (event) => {
      if (event.button !== 0 && event.button !== 2) return;
      if (padResizing) finishGesture();
      event.preventDefault();
      fns.onKeyboard();
      closePanel();
      canvas.focus({ preventScroll: true });
      padActive = padUI = false;
      cursor = point(event);
      if (padGrab) {
        moveGrab();
        finishGesture();
        return;
      }
      beginGesture(state);
      pointer = true;
      activePointer = event.pointerId;
      painted.clear();
      gestureMode = event.button === 2 ? 'erase' : mode;
      canvas.setPointerCapture(event.pointerId);
      const handle = event.button === 0 && !event.shiftKey ? cornerAt(event) : null;
      if (event.button === 0 && event.shiftKey) {
        const target = targetAt(state, cursor.x, cursor.y),
          base = selectionIndices(state);
        if (target?.kind === 'object') toggleObjectSelection(state, target.index);
        else marquee = { start: { ...cursor }, base };
        gestureMode = 'select';
        refreshSelection();
      } else if (handle) {
        gestureMode = 'resize';
        resizeDrag = {
          original: structuredClone(state.level.objects[state.selected]!),
          corner: handle,
        };
      } else if (gestureMode !== 'erase' && grab(event.altKey)) gestureMode = 'grab';
      else if (gestureMode === 'build' || gestureMode === 'erase') atCursor(gestureMode);
      overlayDirty = true;
      updateControls();
    },
    { signal },
  );
  canvas.addEventListener(
    'pointermove',
    (event) => {
      const before = cursor;
      cursor = point(event);
      fns.onKeyboard();
      padActive = false;
      if (pointer && event.pointerId === activePointer) {
        if (resizeDrag) {
          resizeFromCorner(state, resizeDrag.original, resizeDrag.corner, rawPoint(event));
          changed();
        } else if (marquee) {
          selectObjectsInBox(state, marquee.start, cursor, marquee.base);
          refreshSelection();
        } else if (gestureMode === 'grab') moveGrab();
        else paintSegment(before);
      } else if (padGrab) moveGrab();
      overlayDirty = true;
      updateControls();
      if (!pointer && !padGrab && cornerAt(event)) canvas.style.cursor = 'nwse-resize';
    },
    { signal },
  );
  const release = (event: PointerEvent) => {
    if (event.pointerId === activePointer) {
      finishGesture();
      overlayDirty = true;
    }
  };
  canvas.addEventListener('pointerup', release, { signal });
  canvas.addEventListener(
    'pointercancel',
    (event) => {
      if (event.pointerId === activePointer && !cancelHeld()) finishGesture();
    },
    { signal },
  );
  canvas.addEventListener('lostpointercapture', release, { signal });
  canvas.addEventListener('contextmenu', (event) => event.preventDefault(), { signal });
  window.addEventListener('blur', finishGesture, { signal });
  document.addEventListener(
    'visibilitychange',
    () => {
      if (document.hidden) finishGesture();
    },
    { signal },
  );

  function setToolbox(on: boolean) {
    padUI = on;
    wrap.classList.toggle('maker-toolbox-focus', on);
    if (on) {
      finishGesture();
      (panel.hidden
        ? (pieces.querySelector<HTMLButtonElement>('[aria-pressed="true"]') ??
          categories.querySelector('button'))
        : panel.querySelector('button')
      )?.focus({ preventScroll: true });
    } else {
      closePanel();
      canvas.focus({ preventScroll: true });
    }
    updateControls();
  }
  function focusDirection(dx: number, dy: number) {
    const elements = Array.from(
      wrap.querySelectorAll<HTMLElement>('button:not(:disabled), input:not([type="file"])'),
    ).filter((el) => !!el.offsetWidth && !!el.offsetHeight && !el.closest('[hidden]'));
    const active = document.activeElement as HTMLElement,
      a = active?.getBoundingClientRect();
    if (!a || !elements.includes(active)) {
      elements[0]?.focus();
      return;
    }
    const ax = a.left + a.width / 2,
      ay = a.top + a.height / 2;
    let best: HTMLElement | undefined,
      bestScore = Infinity;
    for (const el of elements) {
      if (el === active) continue;
      const r = el.getBoundingClientRect(),
        x = r.left + r.width / 2 - ax,
        y = r.top + r.height / 2 - ay;
      const forward = x * dx + y * dy;
      if (forward <= 2) continue;
      const across = Math.abs(x * dy - y * dx),
        score = forward + across * 3;
      if (score < bestScore) {
        bestScore = score;
        best = el;
      }
    }
    best?.focus({ preventScroll: true });
    best?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
  function moveCursor(dx: number, dy: number) {
    cursor = clamped({ x: cursor.x + dx * state.grid, y: cursor.y + dy * state.grid });
    if (padGrab) moveGrab();
    if (padPainting) atCursor(mode);
    overlayDirty = true;
    updateControls();
  }
  window.addEventListener(
    'keydown',
    (event) => {
      if (disposed || !wrap.isConnected) return;
      if (padResizing) finishGesture();
      fns.onKeyboard();
      const typing =
        event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement;
      if (typing) {
        if (event.code === 'Escape') {
          event.stopImmediatePropagation();
          event.preventDefault();
          canvas.focus();
        }
        return;
      }
      if ((event.ctrlKey || event.metaKey) && (event.code === 'KeyZ' || event.code === 'KeyY')) {
        event.preventDefault();
        finishGesture();
        (event.shiftKey || event.code === 'KeyY' ? redo : undo)(state);
        changed();
        return;
      }
      if (event.code === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!cancelHeld()) setToolbox(!padUI);
        return;
      }
      if (event.code === 'Enter' && event.target === canvas) {
        event.preventDefault();
        testArena(event.shiftKey);
        return;
      }
      if (event.code === 'KeyV') selectMode('grab');
      if (event.code === 'KeyB') selectMode('build');
      if (event.code === 'KeyE') selectMode('erase');
      if (event.code === 'KeyR' && !event.repeat) rotateAtCursor(event.shiftKey);
      if (event.code === 'KeyD' && !event.repeat) startCopy();
      if (event.code === 'Delete' || event.code === 'Backspace') {
        event.preventDefault();
        atCursor('erase');
      }
      if (event.target === canvas) {
        const direction = {
          ArrowLeft: [-1, 0],
          ArrowRight: [1, 0],
          ArrowUp: [0, 1],
          ArrowDown: [0, -1],
        }[event.code];
        if (direction) {
          event.preventDefault();
          moveCursor(direction[0]!, direction[1]!);
        }
        if (event.code === 'Space' && !event.repeat) {
          event.preventDefault();
          primaryAction(false);
        }
      }
    },
    { signal, capture: true },
  );

  function drawScene() {
    if (sim) {
      sim.destroy();
      sim = null;
    }
    sim = createSimWorld({ level: structuredClone(state.level), seed: 1, spawnPlayers: false });
    const cam = createCamera(state.level.bounds);
    cam.zoom = view.scale;
    renderer.render(
      buildFrame(sim, cam, 1, view.width, view.height, null, {
        freezeCamera: true,
        time: 0,
        dt: 0,
      }),
    );
  }
  function screen(x: number, y: number) {
    return {
      x: (x - view.x) * view.scale + view.width / 2,
      y: view.height / 2 - (y - view.y) * view.scale,
    };
  }
  function drawOverlay() {
    const dpr = devicePixelRatio || 1;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.clearRect(0, 0, view.width, view.height);
    const b = state.level.bounds,
      lower = screen(b.x, b.y),
      upper = screen(b.x + b.w, b.y + b.h);
    if (gridVisible) {
      context.strokeStyle = '#ffffff20';
      context.lineWidth = 1;
      context.beginPath();
      for (let x = b.x; x <= b.x + b.w; x += state.grid) {
        const p = screen(x, b.y);
        context.moveTo(p.x, lower.y);
        context.lineTo(p.x, upper.y);
      }
      for (let y = b.y; y <= b.y + b.h; y += state.grid) {
        const p = screen(b.x, y);
        context.moveTo(lower.x, p.y);
        context.lineTo(upper.x, p.y);
      }
      context.stroke();
    }
    context.strokeStyle = '#ffffff66';
    context.lineWidth = 1;
    context.strokeRect(lower.x, upper.y, upper.x - lower.x, lower.y - upper.y);
    const drops = state.level.drops;
    if (state.tool === 'drop-range' && drops) {
      const start = screen(drops.xMin, b.y + b.h),
        end = screen(drops.xMax, b.y);
      context.fillStyle = '#f2c14e20';
      context.fillRect(start.x, start.y, end.x - start.x, end.y - start.y);
      context.fillStyle = '#f2c14e';
      context.font = 'bold 12px "Avenir Next", sans-serif';
      context.fillText('WEAPON DROP ZONE', start.x + 6, start.y + 19);
    }
    for (const [i, s] of state.level.spawns.entries()) {
      const p = screen(s.x, s.y);
      context.save();
      context.translate(p.x, p.y);
      context.lineWidth = i === state.selectedSpawn ? 4 : 2;
      context.strokeStyle = COLORS[i % COLORS.length]!;
      context.fillStyle = COLORS[i % COLORS.length]!;
      context.beginPath();
      context.moveTo(-4, 7);
      context.lineTo(-4, -22);
      context.lineTo(15, -16);
      context.lineTo(-4, -10);
      context.stroke();
      context.beginPath();
      context.arc(0, 7, 7, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = '#151820';
      context.font = 'bold 10px sans-serif';
      context.textAlign = 'center';
      context.fillText(String(i + 1), 0, 10);
      context.restore();
    }
    for (const obj of state.level.objects) {
      if (obj.type !== 'trigger.drop') continue;
      const p = screen(obj.x, obj.y);
      context.fillStyle = '#f2c14e';
      context.font = 'bold 24px sans-serif';
      context.fillText('⇣', p.x - 8, p.y + 8);
    }
    const hovered = targetAt(state, cursor.x, cursor.y),
      holding = padGrab || (pointer && gestureMode === 'grab');
    const selected = selectionIndices(state),
      outlined = new Set(selected);
    if (!holding && hovered?.kind === 'object') outlined.add(hovered.index);
    for (const index of outlined) {
      const obj = state.level.objects[index]!;
      const p = screen(obj.x, obj.y);
      context.save();
      context.translate(p.x, p.y);
      context.rotate(-(obj.angle ?? 0));
      context.strokeStyle = selected.includes(index) ? '#f2c14e' : '#ffffffaa';
      context.lineWidth = selected.includes(index) ? 3 : 1.5;
      const w = (obj.w ?? 2) * view.scale,
        h = (obj.h ?? 1) * view.scale;
      context.strokeRect(-w / 2, -h / 2, w, h);
      context.restore();
    }
    canvas.dataset.resizeHandles = String(selected.length === 1 ? 4 : 0);
    if (selected.length === 1 && !holding) {
      const obj = state.level.objects[selected[0]!]!;
      for (const corner of resizeCorners(obj)) {
        const p = screen(corner.x, corner.y);
        context.fillStyle = '#f2c14e';
        context.strokeStyle = '#161b24';
        context.lineWidth = 2;
        context.fillRect(p.x - 6, p.y - 6, 12, 12);
        context.strokeRect(p.x - 6, p.y - 6, 12, 12);
      }
    }
    if (marquee) {
      const a = screen(marquee.start.x, marquee.start.y),
        b = screen(cursor.x, cursor.y);
      context.fillStyle = '#f2c14e22';
      context.strokeStyle = '#f2c14e';
      context.lineWidth = 2;
      context.fillRect(a.x, a.y, b.x - a.x, b.y - a.y);
      context.strokeRect(a.x, a.y, b.x - a.x, b.y - a.y);
    }
    const p = screen(cursor.x, cursor.y);
    context.save();
    context.translate(p.x, p.y);
    context.strokeStyle = mode === 'erase' ? '#ffaca2' : '#fff';
    context.lineWidth = 2;
    if (
      mode === 'build' &&
      !holding &&
      !hovered &&
      state.tool !== 'spawn' &&
      state.tool !== 'drop-range'
    ) {
      const ghost = makeObject(state.tool, cursor.x, cursor.y, state.grid),
        w = (ghost.w ?? 2) * view.scale,
        h = (ghost.h ?? 1) * view.scale;
      context.fillStyle = '#ffffff38';
      context.fillRect(-w / 2, -h / 2, w, h);
      context.setLineDash([5, 4]);
      context.strokeRect(-w / 2, -h / 2, w, h);
      context.setLineDash([]);
    }
    context.beginPath();
    context.arc(0, 0, padActive ? 12 : 6, 0, Math.PI * 2);
    context.moveTo(-18, 0);
    context.lineTo(-9, 0);
    context.moveTo(9, 0);
    context.lineTo(18, 0);
    context.moveTo(0, -18);
    context.lineTo(0, -9);
    context.moveTo(0, 9);
    context.lineTo(0, 18);
    context.stroke();
    context.restore();
  }
  function resized() {
    if (disposed) return;
    const r = stage.getBoundingClientRect();
    view = editorViewport(state.level.bounds, Math.max(1, r.width), Math.max(1, r.height));
    const dpr = devicePixelRatio || 1;
    canvas.width = Math.floor(view.width * dpr);
    canvas.height = Math.floor(view.height * dpr);
    renderer.resize(view.width, view.height);
    dirty = overlayDirty = true;
  }
  const observer = new ResizeObserver(resized);
  observer.observe(stage);
  function poll(time: number) {
    if (disposed) return;
    const input = fns.getInput(),
      allPads = navigator.getGamepads?.() ?? [],
      pad = input.kind === 'gamepad' ? allPads[input.index] : null,
      nextInputKey = `${input.kind}:${input.kind === 'gamepad' ? `${input.index}:${input.id}:${input.family}` : ''}:${fns.getGlyphs()}`;
    if (nextInputKey !== inputKey) {
      inputKey = nextInputKey;
      overlayDirty = true;
      updateControls();
    }
    if (pad) {
      const previousButtons = padButtons.get(pad.index) ?? [];
      if (previousPad !== pad.index) previousDirection = '';
      previousPad = pad.index;
      const buttons = pad.buttons.map((b) => b.pressed),
        edge = (i: number) => buttons[i] && !previousButtons[i];
      if (padResizing && [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].some(edge)) finishGesture();
      const dx =
        buttons[14] || (pad.axes[0] ?? 0) < -0.55
          ? -1
          : buttons[15] || (pad.axes[0] ?? 0) > 0.55
            ? 1
            : 0;
      const dy =
        buttons[12] || (pad.axes[1] ?? 0) < -0.55
          ? 1
          : buttons[13] || (pad.axes[1] ?? 0) > 0.55
            ? -1
            : 0;
      const direction = `${dx},${dy}`;
      if (dx || dy || buttons.some((on, i) => on && !previousButtons[i])) {
        padActive = true;
        overlayDirty = true;
        updateControls();
      }
      if ((dx || dy) && (direction !== previousDirection || time >= repeatAt)) {
        if (padUI) focusDirection(dx, -dy);
        else moveCursor(dx, dy);
        repeatAt = time + (direction !== previousDirection ? 230 : 80);
      }
      previousDirection = direction;
      if (edge(1)) {
        if (!cancelHeld()) setToolbox(!padUI);
      } else if (edge(8)) setToolbox(!padUI);
      if (edge(9)) {
        testArena(!!buttons[10]);
        if (disposed) return;
      }
      if (edge(4) || edge(5)) {
        const i = PIECES.findIndex((p) => p.id === state.tool);
        pickTool(PIECES[(i + (edge(4) ? -1 : 1) + PIECES.length) % PIECES.length]!.id);
      }
      if (edge(3)) {
        finishGesture();
        undo(state);
        changed('Undid the last edit.');
      }
      if (edge(0)) {
        if (padUI) (document.activeElement as HTMLElement)?.click();
        else primaryAction(true);
      }
      if (disposed) return;
      if (!buttons[0] && previousButtons[0] && padPainting) finishGesture();
      if (edge(2) && !padUI) {
        finishGesture();
        atCursor('erase');
      }
      if (edge(10) && !buttons[9] && !padUI) toggleAtCursor();
      if (edge(11) && !padUI) startCopy();
      if (!padUI && (edge(6) || edge(7))) rotateAtCursor(!!edge(6));
      const rw = (pad.axes[2] ?? 0) > 0.6 ? 1 : (pad.axes[2] ?? 0) < -0.6 ? -1 : 0;
      const rh = (pad.axes[3] ?? 0) < -0.6 ? 1 : (pad.axes[3] ?? 0) > 0.6 ? -1 : 0;
      const sizeDirection = `${rw},${rh}`;
      if (!padUI && !padGrab && !pointer && selectionIndices(state).length === 1 && (rw || rh)) {
        if (!padResizing) {
          beginGesture(state);
          padResizing = true;
        }
        if (sizeDirection !== resizeDirection || time >= resizeRepeatAt) {
          const obj = state.level.objects[state.selected]!;
          resizeSelected(state, (obj.w ?? 2) + rw * state.grid, (obj.h ?? 1) + rh * state.grid);
          changed();
          resizeRepeatAt = time + (sizeDirection !== resizeDirection ? 250 : 120);
        }
      } else if (padResizing) {
        padResizing = false;
        finishGesture();
      }
      resizeDirection = sizeDirection;
    } else if (padResizing) finishGesture();
    else if (previousPad >= 0 && !allPads[previousPad]) {
      finishGesture();
      previousPad = -1;
      previousDirection = '';
    }
    for (const p of allPads)
      if (p)
        padButtons.set(
          p.index,
          p.buttons.map((b) => b.pressed),
        );
    if (dirty) {
      const r = stage.getBoundingClientRect();
      view = editorViewport(state.level.bounds, Math.max(1, r.width), Math.max(1, r.height));
      try {
        drawScene();
      } catch {
        announce('This piece could not be previewed. Undo the last edit to recover.');
      }
      dirty = false;
    }
    if (overlayDirty) {
      drawOverlay();
      overlayDirty = false;
    }
    raf = requestAnimationFrame(poll);
  }
  paintShelf();
  changed();
  resized();
  canvas.focus({ preventScroll: true });
  raf = requestAnimationFrame(poll);
  return {
    dispose() {
      if (disposed) return;
      finishGesture();
      disposed = true;
      abort.abort();
      observer.disconnect();
      cancelAnimationFrame(raf);
      renderer.dispose();
      sim?.destroy();
      sim = null;
      wrap.remove();
    },
  };
}
